// All threads of one room, most recently active first — the view for rooms where the work happens
// in threads (one per build, job or topic) and the main timeline is just headlines.
import React, { useEffect, useState } from 'react';
import { View, Pressable, FlatList, StyleSheet, ActivityIndicator, RefreshControl } from 'react-native';
import { Text } from '../ui/Text';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import { useRoom, useIsAgent, useThreadRoots, loadThreadRoots, threadReplies, threadTitle, senderName, MEvent } from '../matrix';
import { usePrefs } from '../prefs';
import { Avatar } from '../ui/Avatar';
import { radius, makeStyles, useTheme } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'Threads'>;

function fmtWhen(ts: number) {
  const d = new Date(ts); const now = new Date();
  return d.toDateString() === now.toDateString() ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function ThreadsScreen({ route, navigation }: Props) {
  const { roomId } = route.params;
  const theme = useTheme(); const s = useStyles();
  const { userId } = useAuth();
  const room = useRoom(roomId);
  const isAgent = useIsAgent();
  const { humanBadge } = usePrefs();
  const threads = useThreadRoots(roomId);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { navigation.setOptions({ title: room?.name ? `Threads · ${room.name}` : 'Threads' }); }, [room?.name]);
  useEffect(() => { loadThreadRoots(roomId).catch(() => {}).finally(() => setLoading(false)); }, [roomId]);
  const refresh = () => { setRefreshing(true); loadThreadRoots(roomId).catch(() => {}).finally(() => setRefreshing(false)); };

  const open = (t: MEvent) => navigation.navigate('Thread', { roomId, rootId: t.id });

  const renderItem = ({ item: t }: { item: MEvent }) => {
    const replies = threadReplies(roomId, t.id);
    const n = Math.max(t.threadCount || 0, replies.length);
    const latest = Math.max(t.threadLatestTs || 0, ...replies.map((r) => r.ts), t.ts);
    const who = [...new Set([t.sender, ...replies.map((r) => r.sender)])];
    const names = who.map((u) => (u === userId ? 'You' : senderName(room, u)));
    const youIn = who.includes(userId || '');
    const starter = t.sender === userId ? 'You' : senderName(room, t.sender);
    const unread = room?.threadUnread?.[t.id] || 0;
    return (
      <Pressable onPress={() => open(t)} style={({ pressed }) => [s.row, pressed && { backgroundColor: theme.surface2 }]}>
        <Avatar name={senderName(room, t.sender)} mxc={room?.memberAvatars[t.sender]} kind={isAgent(t.sender) ? 'agent' : 'human'} size={40} humanBadge={humanBadge} me={t.sender === userId} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={s.head}>
            <Text style={[s.title, unread > 0 && { fontWeight: '800' }]} numberOfLines={2}>{threadTitle(t)}</Text>
            {unread > 0 ? <View style={s.newPill}><Text style={s.newText}>{unread} new</Text></View> : <Text style={s.when}>{fmtWhen(latest)}</Text>}
          </View>
          <Text style={s.meta} numberOfLines={1}>
            {t.thread?.kind === 'conversation' ? 'Conversation · ' : ''}{n} {n === 1 ? 'reply' : 'replies'} · {names.slice(0, 3).join(', ')}{names.length > 3 ? ` +${names.length - 3}` : ''}
          </Text>
          {youIn && <Text style={s.mine}>You're in this thread</Text>}
        </View>
        <Text style={s.chev}>›</Text>
      </Pressable>
    );
  };

  return (
    <SafeAreaView style={s.root} edges={['left', 'right', 'bottom']}>
      {loading && threads.length === 0 ? (
        <View style={s.center}><ActivityIndicator color={theme.accent} /></View>
      ) : threads.length === 0 ? (
        <View style={s.center}>
          <Text style={s.emptyTitle}>No threads yet</Text>
          <Text style={s.empty}>Long-press any message in the room and choose “Reply in thread”. Agents will also start threads here, one per build or job. To talk to an agent afresh, tap ＋ New on Home.</Text>
        </View>
      ) : (
        <FlatList
          data={threads} keyExtractor={(t) => t.id} renderItem={renderItem}
          contentContainerStyle={{ paddingVertical: 8 }}
          ListHeaderComponent={<Text style={s.orient}>Newest activity first. Tap a thread to open it.</Text>}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={theme.muted} />}
          ItemSeparatorComponent={() => <View style={s.sep} />}
        />
      )}
    </SafeAreaView>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 8 },
  emptyTitle: { color: theme.ink, fontSize: 17, fontWeight: '700' },
  empty: { color: theme.muted, fontSize: 14.5, textAlign: 'center', lineHeight: 20 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12 },
  sep: { height: 1, backgroundColor: theme.border, marginLeft: 68 },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  title: { flex: 1, color: theme.ink, fontSize: 15.5, fontWeight: '600', lineHeight: 20 },
  when: { color: theme.faint, fontSize: 12, fontFamily: theme.mono, paddingTop: 3 },
  newPill: { backgroundColor: theme.accent, borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 2, alignSelf: 'flex-start' },
  newText: { color: theme.accentInk, fontSize: 11, fontWeight: '800' },
  meta: { color: theme.muted, fontSize: 13, marginTop: 3 },
  mine: { color: theme.accent, fontSize: 12, marginTop: 2, fontWeight: '600' },
  chev: { color: theme.faint, fontSize: 22 },
  orient: { color: theme.faint, fontSize: 12.5, paddingHorizontal: 16, paddingBottom: 6 },
}));
